/**
 * [TD-89 v2 Verdict O.A] Per-manager kebab dropdown for commissioner actions.
 *
 * Single item in v2: Convert to CPU (Capability E).
 *
 * Mounts on each manager card/row across:
 *   - Lobby manager list
 *   - DraftRoom + SnakeDraftRoom manager tiles
 *   - Standings rows (season-time)
 *
 * Visibility: `isCommissioner && target !== commissioner && !target.is_cpu &&
 *             league.status NOT IN {SEASON_COMPLETE, ARCHIVED}`.
 */
import { useEffect, useRef, useState } from "react";
import { MoreVertical, Cpu } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { authHeaders } from "../../contexts/AuthContext";
import ConfirmModal from "./ConfirmModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BLOCKED_CONVERT = new Set(["SEASON_COMPLETE", "ARCHIVED"]);

export default function ManagerActionMenu({
  league,
  manager,   // { user_id, first_name, last_name, team_name, is_cpu }
  isCommissioner,
  isDark,
  onMutated,
}) {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const targetIsSelf = manager?.user_id === league?.commissioner_id;
  const targetIsAlreadyCpu = !!manager?.is_cpu;
  const statusBlocks = BLOCKED_CONVERT.has(league?.status);
  const canConvert = isCommissioner && !targetIsSelf && !targetIsAlreadyCpu && !statusBlocks;
  if (!canConvert) return null;

  const stop = (e) => e.stopPropagation();

  const handleConvert = async () => {
    try {
      const { data } = await axios.post(
        `${API}/commissioner/${league.id}/convert-manager-to-cpu`,
        { target_user_id: manager.user_id },
        { headers: authHeaders() },
      );
      toast.success(
        data?.new_team_name
          ? `${manager.first_name} replaced by CPU · team renamed to "${data.new_team_name}"`
          : `${manager.first_name} replaced by CPU`
      );
      onMutated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Conversion failed");
    }
  };

  const fullName = [manager?.first_name, manager?.last_name].filter(Boolean).join(" ").trim() || "this manager";
  const teamName = manager?.team_name || "their team";
  const status = league?.status;
  const bodyCopy =
    status === "SETUP" || status === "LOBBY"
      ? `${fullName} will be replaced by a CPU. Since the draft hasn't started, their team will get a fresh CPU-pool name.`
      : status === "DRAFTING" || status === "PAUSED"
      ? `${fullName} will be replaced by a CPU. Their team "${teamName}" keeps its draft picks so far - the CPU AI takes over for the remaining picks.`
      : `${fullName} will be replaced by a CPU. Their team "${teamName}" keeps its roster, record, and current-week lineup. The CPU takes over from next week onward.`;

  const itemCls = `flex items-center gap-2 px-3 py-2 font-body text-sm w-full text-left transition-colors ${
    isDark ? "text-[#F5F5F5] hover:bg-white/[0.06]" : "text-[#0A0A0A] hover:bg-black/[0.05]"
  }`;
  const ddBg = isDark ? "bg-[#1C1C1C] border-[#252525]" : "bg-white border-black/[0.10]";

  return (
    <div ref={rootRef} className="relative inline-block" onClick={stop}>
      <button
        data-testid={`manager-action-menu-trigger-${manager.user_id}`}
        onClick={(e) => { stop(e); setOpen((v) => !v); }}
        className={`p-1 rounded-md transition-colors ${
          isDark ? "text-[#A0A0A0] hover:text-[#FF6B00] hover:bg-white/[0.06]" : "text-[#666] hover:text-[#FF6B00] hover:bg-black/[0.05]"
        }`}
        aria-label="Manager actions"
      >
        <MoreVertical size={14} />
      </button>
      {open && (
        <div
          data-testid={`manager-action-menu-dropdown-${manager.user_id}`}
          className={`absolute right-0 top-full mt-1 w-48 rounded-lg border shadow-xl overflow-hidden z-20 ${ddBg}`}
        >
          <button
            data-testid={`manager-action-menu-item-convert-${manager.user_id}`}
            onClick={(e) => { stop(e); setOpen(false); setConfirm(true); }}
            className={itemCls}
          >
            <Cpu size={14} /> Convert to CPU
          </button>
        </div>
      )}
      <ConfirmModal
        open={confirm}
        onCancel={() => setConfirm(false)}
        onConfirm={async () => { await handleConvert(); setConfirm(false); }}
        title={`Convert ${fullName} to CPU?`}
        body={<>{bodyCopy} <strong>This cannot be undone.</strong></>}
        confirmLabel="Convert to CPU"
        destructive
        isDark={isDark}
        testIdPrefix={`manager-action-confirm-convert-${manager.user_id}`}
      />
    </div>
  );
}
